import React, { useEffect, useState, useCallback, useRef } from 'react';
import Container from '../../Container/Container';
import { useDispatch, useSelector } from 'react-redux';
import { get } from "../../ApiServices/ApiServices";
import { useNavigate } from 'react-router';
import Loader from '../Component/Loader';
import { toast } from 'react-toastify';
import moment from 'moment';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import 'bootstrap-daterangepicker/daterangepicker.css';

function UsersList() {
    const navigate = useNavigate();
    const profile = useSelector((state) => state.profile.profile);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [apiLoading, setApiLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [count, setCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [Status, setStatus] = useState([])

    const [dateRange, setDateRange] = useState({
        startDate: null,
        endDate: null,
    });
    const [displayLabel, setDisplayLabel] = useState('');
    const [orderStatusFilter, setOrderStatusFilter] = useState('');



    const ActiveStatus = async () => {
        try {
            setLoading(true)
            const api = `get-statuses`;
            const response = await get(api);
            if (response?.message === "success") {
                setStatus(response?.data)
            }
        } catch (err) {
            console.error('Error:', err);
            if (err?.message == "Invalid or expired token" || err?.message == "Invalid Token") {
                localStorage.removeItem('token');
                navigate('/login');
            }
        } finally {
        }
    };

    useEffect(() => {
        ActiveStatus()
    }, [])
    // Fixed onDateChange function
    const onDateChange = (dates) => {
        if (dates.startDate && dates.endDate) {
            setDateRange({
                startDate: dates.startDate,
                endDate: dates.endDate
            });
        } else {
            setDateRange({
                startDate: null,
                endDate: null
            });
        }
        setCurrentPage(1);
    };

    // Fixed handleApply function
    const handleApply = (event, picker) => {
        const startDate = picker.startDate;
        const endDate = picker.endDate;

        setDateRange({
            startDate: startDate,
            endDate: endDate
        });
        setDisplayLabel(`${startDate.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')}`);
        setCurrentPage(1); // Reset to first page when date changes
    };

    // Fixed handleCancel function
    const handleCancel = (event, picker) => {
        setDateRange({
            startDate: null,
            endDate: null,
        });
        setDisplayLabel('');
        setCurrentPage(1); // Reset to first page
    };

    const debounce = (func, delay) => {
        let timer;
        return function (...args) {
            clearTimeout(timer);
            timer = setTimeout(() => func.apply(this, args), delay);
        };
    };

    const handleSearchDebounced = useCallback(
        debounce((value) => {
            setDebouncedSearchTerm(value);
            setCurrentPage(1);
        }, 500),
        []
    );

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        handleSearchDebounced(value);
    };

    const handleOrderStatusChange = (e) => {
        setOrderStatusFilter(e.target.value);
        setCurrentPage(1);
    };

    // Fixed getUsers function
    const getUsers = async () => {
        setApiLoading(true);
        try {
            let api = `admin-users?page_number=${currentPage}&sortby=createdAt&orderby=asc&size=${itemsPerPage}&search=${debouncedSearchTerm}&order_status=${orderStatusFilter}`;

            // Add date parameters if dates are selected
            if (dateRange?.startDate && moment(dateRange.startDate).isValid()) {
                api += `&start_date=${moment(dateRange.startDate).format('YYYY-MM-DD')}`;
            }

            if (dateRange?.endDate && moment(dateRange.endDate).isValid()) {
                api += `&end_date=${moment(dateRange.endDate).format('YYYY-MM-DD')}`;
            }

            console.log('API URL:', api); // Debug log

            const response = await get(api);

            if (response?.status === true) {
                setUsers(response.data?.rows || []);
                setCount(response?.data?.count || 0);
                setTotalPages(Math.ceil(response.data?.count / itemsPerPage));
            }
        } catch (err) {
            if (err?.message === "Invalid or expired token" || err?.message === "Invalid Token") {
                localStorage.removeItem('token');
                navigate('/login');
            }
            console.error("Error fetching users:", err);
            toast.error('Error fetching users');
        } finally {
            setApiLoading(false);
        }
    };

    useEffect(() => {
        getUsers();
    }, [currentPage, itemsPerPage, debouncedSearchTerm, dateRange, orderStatusFilter]); // Use dateRange object as dependency

    // Rest of your component remains the same...
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleItemsPerPageChange = (e) => {
        const newItemsPerPage = Number(e.target.value);
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
        setTotalPages(Math.ceil(count / newItemsPerPage));
    };

    const renderPaginationButtons = () => {
        const buttons = [];
        const maxVisibleButtons = 5;
        let startPage, endPage;

        if (totalPages <= maxVisibleButtons) {
            startPage = 1;
            endPage = totalPages;
        } else {
            const maxButtonsBeforeCurrent = Math.floor(maxVisibleButtons / 2);
            const maxButtonsAfterCurrent = Math.ceil(maxVisibleButtons / 2) - 1;

            if (currentPage <= maxButtonsBeforeCurrent) {
                startPage = 1;
                endPage = maxVisibleButtons;
            } else if (currentPage + maxButtonsAfterCurrent >= totalPages) {
                startPage = totalPages - maxVisibleButtons + 1;
                endPage = totalPages;
            } else {
                startPage = currentPage - maxButtonsBeforeCurrent;
                endPage = currentPage + maxButtonsAfterCurrent;
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <button
                    key={i}
                    className={`btn btn-sm ${currentPage === i ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => handlePageChange(i)}
                    disabled={apiLoading}
                >
                    {i}
                </button>
            );
        }
        return buttons;
    };


    return (
        <Container>
            <div className="nk-content">
                <div className="container-fluid">
                    <div className="nk-content-inner">
                        <div className="nk-content-body">
                            <div className="nk-block-head nk-block-head-sm">
                                <div className="nk-block-between">
                                    <div className="nk-block-head-content">
                                        <h3 className="nk-block-title page-title">Drop shipping orders</h3>
                                    </div>
                                </div>
                            </div>

                            <div className="card card-full">
                                <div className="card-inner">
                                    <div className="card-title-group mb-3">
                                        <div className="card-tools" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                                            <div className="form-control-wrap">
                                                <div className="form-icon form-icon-right">
                                                    <em className="icon ni ni-search"></em>
                                                </div>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search by Account# or Order#"
                                                    value={searchTerm}
                                                    onChange={handleSearch}
                                                    style={{ minWidth: '250px' }}
                                                />
                                            </div>

                                            <div className="form-control-wrap">
                                                <select
                                                    className="form-control"
                                                    value={orderStatusFilter}
                                                    onChange={handleOrderStatusChange}
                                                    style={{ minWidth: '200px' }}
                                                >
                                                    <option value="">Filter by Active Status</option>
                                                    <option value="">All</option>
                                                    {
                                                        Status?.map((data) => (
                                                            <option key={data.name} value={data.name}>
                                                                {data?.name}
                                                            </option>
                                                        ))
                                                    }
                                                </select>
                                            </div>

                                            <div className="form-control-wrap" style={{ position: 'relative' }}>
                                                <DateRangePicker
                                                    initialSettings={{
                                                        startDate: dateRange.startDate || moment(),
                                                        endDate: dateRange.endDate || moment(),
                                                        locale: {
                                                            format: 'MMM D, YYYY',
                                                            cancelLabel: 'Clear',
                                                            applyLabel: 'Apply',
                                                        },
                                                        ranges: {
                                                            'Today': [moment(), moment()],
                                                            'Yesterday': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
                                                            'Last 7 Days': [moment().subtract(6, 'days'), moment()],
                                                            'Last 30 Days': [moment().subtract(29, 'days'), moment()],
                                                            'This Month': [moment().startOf('month'), moment().endOf('month')],
                                                            'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')]
                                                        },
                                                        opens: 'right',
                                                        autoApply: false,
                                                        alwaysShowCalendars: true,
                                                        showCustomRangeLabel: true,
                                                    }}
                                                    onApply={handleApply}
                                                    onCancel={handleCancel}
                                                >
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Select date range"
                                                        value={displayLabel}
                                                        readOnly
                                                        style={{
                                                            cursor: 'pointer',
                                                            backgroundColor: '#fff',
                                                            border: '1px solid #dbdfea',
                                                            borderRadius: '4px',
                                                            padding: '0.375rem 0.75rem',
                                                            minWidth: '250px',
                                                            paddingRight: '30px',
                                                            color: '#35495e',
                                                        }}
                                                    />
                                                </DateRangePicker>
                                                {displayLabel && (
                                                    <span
                                                        className="clear-icon"
                                                        onClick={handleCancel}
                                                        style={{
                                                            position: 'absolute',
                                                            right: '10px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            cursor: 'pointer',
                                                            zIndex: 10
                                                        }}
                                                    >
                                                        <em className="icon ni ni-cross"></em>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {apiLoading ? (
                                        <Loader />
                                    ) : (
                                        <>
                                            <div className="table-responsive">
                                                <table className="table table-striped">
                                                    <thead>
                                                        <tr>
                                                            <th scope="col">Acc#</th>
                                                            <th scope="col">Ticket</th>
                                                            <th scope="col">Order</th>
                                                            <th scope="col">Weekending Date</th>
                                                            <th scope="col">Name</th>
                                                            <th scope="col">Address</th>
                                                            <th scope="col">Phone</th>
                                                            <th scope="col">Order Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {users.length > 0 ? (
                                                            users.map((user) => (
                                                                <tr key={user.id}>
                                                                    <td>{user.account || 'N/A'}</td>
                                                                    <td
                                                                        style={{ cursor: "pointer", fontWeight: "bold" }}
                                                                        onClick={() => {
                                                                            navigate(
                                                                                user?.order_type == "Single Order" ? "/order-detail" : "/Member-detail",
                                                                                {
                                                                                    state:
                                                                                        user?.order_type == "Single Order"
                                                                                            ? { orderId: user?.order_id }
                                                                                            : { eventId: user?.order_id },
                                                                                }
                                                                            );
                                                                        }}
                                                                    >
                                                                        {user.ticket || 'N/A'}
                                                                    </td>
                                                                    <td>{user.order_type || 'N/A'}</td>
                                                                    <td>
                                                                        {/* {user.event_date ? moment(user.event_date).format("MMM D, YYYY") : 'N/A'} */}

                                                                        {
                                                                            (() => {
                                                                                const eventDate = moment(user?.event_date);
                                                                                const currentDay = eventDate.isoWeekday(); // 1 = Monday, ..., 6 = Saturday, 7 = Sunday
                                                                                if (currentDay === 7) {
                                                                                    // Sunday → Previous Saturday
                                                                                    return eventDate.clone().subtract(1, "day").format("MMM D, YYYY");
                                                                                } else if (currentDay === 1) {
                                                                                    // Monday → Previous Saturday
                                                                                    return eventDate.clone().subtract(2, "days").format("MMM D, YYYY");
                                                                                } else if (currentDay === 6) {
                                                                                    // Saturday → Current Saturday
                                                                                    return eventDate.format("MMM D, YYYY");
                                                                                } else {
                                                                                    // Tuesday to Friday → Upcoming Saturday
                                                                                    return eventDate.clone().isoWeekday(6).format("MMM D, YYYY");
                                                                                }
                                                                            })()
                                                                        }
                                                                    </td>
                                                                    <td>{user.customer_name} {user.last_name}</td>
                                                                    <td>
                                                                        <div>
                                                                            {user.shipping_address || ''}
                                                                            {user.shipping_address && <div>{user.shipping_address_line2}</div>}
                                                                            <div>
                                                                                {user.shipping_city || ''}
                                                                                {user.shipping_state || ''}
                                                                                {user.shipping_zip && ` ${user.shipping_zip}`}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td>{user.phone || 'N/A'}</td>
                                                                    <td className="tableMinWidth120" >
                                                                        <span className="badge openBadge text-capitalize font-revert bg-warning">
                                                                            {user?.order_status}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="8" className="text-center py-4">
                                                                    {debouncedSearchTerm || orderStatusFilter || displayLabel
                                                                        ? 'No users found matching your filters'
                                                                        : 'No users found'}
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {count > 0 && (
                                                <div className="nk-block-between-md g-3 mt-3">
                                                    <div className="g">
                                                        <div className="pagination d-flex gap-2 align-items-center">
                                                            <button
                                                                className={`btn btn-sm ${currentPage === 1 ? 'btn-disabled' : 'btn-primary'}`}
                                                                onClick={() => handlePageChange(currentPage - 1)}
                                                                disabled={currentPage === 1 || apiLoading}
                                                            >
                                                                Previous
                                                            </button>
                                                            {renderPaginationButtons()}
                                                            <button
                                                                className={`btn btn-sm ${currentPage === totalPages ? 'btn-disabled' : 'btn-primary'}`}
                                                                onClick={() => handlePageChange(currentPage + 1)}
                                                                disabled={currentPage === totalPages || apiLoading}
                                                            >
                                                                Next
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="g">
                                                        <div className="pagination-goto d-flex align-items-center gap-3">
                                                            <div>Showing</div>
                                                            <div>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={itemsPerPage}
                                                                    onChange={handleItemsPerPageChange}
                                                                    disabled={apiLoading}
                                                                >
                                                                    {[10, 20, 50, 100].map(size => (
                                                                        <option key={size} value={size}>{size}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                            <div>of {count} items</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div >
        </Container >
    )
}

export default UsersList;